import { searchPluginKey } from './extensions/search_highlight'

let activeBar = null

// A visible outline host handles this event; focus mode and standalone editors
// retain the inline Find surface without opening a hidden sidebar.
export function openDocumentFind(container, getEditors, options = {}) {
  const event = new CustomEvent('editor:find', { bubbles: true, cancelable: true, detail: { container, getEditors, options } })
  container.dispatchEvent(event)
  return event.defaultPrevented ? event.detail.bar : buildFindReplaceBar(container, getEditors, options)
}

export function buildFindReplaceBar(container, getEditors, { showReplace = false, mount, onClose, onFocus } = {}) {
  if (activeBar?.element.isConnected && activeBar.container === container && activeBar.mount === mount) {
    activeBar.focus(showReplace)
    return activeBar
  }
  activeBar?.close({ restoreFocus: false })
  const returnFocus = document.activeElement
  const returnEditor = getEditors().find(editor => editor?.isFocused) || getEditors()[0]
  const bar = document.createElement('div')
  bar.className = `find-replace-bar${mount ? ' find-replace-panel' : ''}`
  bar.setAttribute('role', 'search')
  bar.setAttribute('aria-label', 'Find in document')
  bar.innerHTML = `
    <div class="find-replace-title"><strong>Find a passage</strong><button type="button" class="find-replace-btn" data-action="close" aria-label="Close Find" title="Close (Escape)"><span class="fas fa-times" aria-hidden="true"></span></button></div>
    <label class="find-replace-input-group"><span class="sr-only">Search document</span><span class="find-replace-icon fas fa-search" aria-hidden="true"></span><input type="search" class="find-replace-input" aria-label="Search document" placeholder="Find in text…" autocomplete="off" spellcheck="false"></label>
    <div class="find-replace-row find-replace-navigation">
      <span class="find-replace-count" role="status"></span>
      <button type="button" class="find-replace-btn" title="Previous (Shift+Enter)" aria-label="Previous match" data-action="prev"><span class="fas fa-chevron-up" aria-hidden="true"></span></button>
      <button type="button" class="find-replace-btn" title="Next (Enter)" aria-label="Next match" data-action="next"><span class="fas fa-chevron-down" aria-hidden="true"></span></button>
      <button type="button" class="find-replace-btn find-replace-case-btn" title="Match case" aria-label="Match case" aria-pressed="false" data-action="case">Aa</button>
    </div>
    <p class="find-replace-scope" role="status"></p>
    <button type="button" class="find-replace-btn find-replace-text-btn" data-action="toggleReplace" aria-expanded="${showReplace}">Replace…</button>
    <div class="find-replace-replace-row ${showReplace ? '' : 'd-none'}">
      <label class="find-replace-input-group"><span class="sr-only">Replace with</span><input type="text" class="find-replace-replace-input" aria-label="Replace with" placeholder="Replace with…" autocomplete="off" spellcheck="false"></label>
      <div class="find-replace-row"><button type="button" class="find-replace-btn find-replace-text-btn" data-action="replace">Replace</button><button type="button" class="find-replace-btn find-replace-text-btn" data-action="replaceAll">Replace all</button></div>
      <p class="find-replace-replace-scope"></p>
    </div>
    <details class="find-replace-results"><summary>Passage results</summary><div class="find-replace-result-list"></div></details>`
  const input = bar.querySelector('.find-replace-input')
  const replacement = bar.querySelector('.find-replace-replace-input')
  const replaceRow = bar.querySelector('.find-replace-replace-row')
  const count = bar.querySelector('.find-replace-count')
  const scope = bar.querySelector('.find-replace-scope')
  const resultList = bar.querySelector('.find-replace-result-list')
  const details = bar.querySelector('details')
  details.open = window.innerWidth > 600
  let caseSensitive = false, matches = [], index = -1, closed = false, timer, refreshing = false
  let targetInvalidated = false, replacing = false
  const watched = new Set()
  const liveEditors = () => getEditors().filter(editor => editor && !editor.isDestroyed && editor.view.dom.isConnected)
    .sort((a, b) => a.view.dom.compareDocumentPosition(b.view.dom) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1)
  const frames = () => Array.from(container.querySelectorAll('.sections-list > turbo-frame'))
  const coverage = () => {
    const editors = liveEditors(), total = frames().length
    const sectioned = total > 0 || !!container.querySelector('[data-controller~="section-editor"]')
    const loaded = sectioned ? frames().filter(frame => editors.some(editor => frame.contains(editor.view.dom))).length : editors.length
    return { sectioned, loaded, total: sectioned ? total : 1, complete: sectioned ? total > 0 && loaded === total : editors.length > 0,
      limited: editors.some(editor => editor.storage.searchHighlight?.truncated) }
  }
  function watchEditors() {
    const editors = liveEditors()
    watched.forEach(editor => { if (!editors.includes(editor)) { editor.off('transaction', trackDocumentChange); watched.delete(editor) } })
    editors.forEach(editor => { if (!watched.has(editor)) { watched.add(editor); editor.on('transaction', trackDocumentChange) } })
    return editors
  }
  function trackDocumentChange({ editor, transaction }) {
    if (!transaction.docChanged) return
    const selected = matches[index]
    if (!replacing && selected?.editor === editor) {
      const from = transaction.mapping.mapResult(selected.from, 1)
      const to = transaction.mapping.mapResult(selected.to, -1)
      if (from.deleted || to.deleted || from.pos >= to.pos) {
        targetInvalidated = true
        index = -1
      } else {
        selected.from = from.pos
        selected.to = to.pos
      }
    }
    scheduleRefresh()
  }
  function refresh({ navigate = false, preserve = true } = {}) {
    if (closed || refreshing) return
    clearTimeout(timer)
    refreshing = true
    if (!preserve) targetInvalidated = false
    const previous = preserve ? matches[index] : null
    matches = []
    watchEditors().forEach(editor => {
      editor.commands.setSearchQuery({query: input.value, caseSensitive})
      ;(editor.storage.searchHighlight?.matches || []).forEach(match => matches.push({...match, editor}))
    })
    index = previous ? matches.findIndex(match => match.editor === previous.editor && match.from === previous.from && match.to === previous.to) : -1
    if (previous && index < 0) targetInvalidated = true
    if (index < 0 && matches.length && !targetInvalidated) index = 0
    updateHighlights()
    render()
    refreshing = false
    if (navigate && matches[index]) reveal(matches[index])
  }
  function scheduleRefresh() { clearTimeout(timer); timer = setTimeout(() => refresh(), 80) }
  function updateHighlights() {
    const current = matches[index]
    liveEditors().forEach(editor => {
      const local = current?.editor === editor ? editor.storage.searchHighlight.matches.findIndex(match => match.from === current.from && match.to === current.to) : -1
      editor.view.dispatch(editor.state.tr.setMeta(searchPluginKey, {type:'navigate',index:local}))
    })
  }
  function reveal(match) {
    const target = match.editor.view.dom.querySelector('.search-match-current')
    if (!target) return
    const toolbar = container.querySelector('.tiptap-fixed-toolbar')
    let owner = target.parentElement
    while (owner && owner !== document.body && owner !== document.documentElement) {
      if (/^(auto|scroll|hidden)$/.test(getComputedStyle(owner).overflowY) && owner.scrollHeight > owner.clientHeight) break
      owner = owner.parentElement
    }
    const nested = owner && owner !== document.body && owner !== document.documentElement
    const top = Math.max(0, nested ? owner.getBoundingClientRect().top : 0, toolbar?.getBoundingClientRect().bottom || 0, !mount ? bar.getBoundingClientRect().bottom : 0) + 10
    const bottom = Math.min(innerHeight, nested ? owner.getBoundingClientRect().bottom : innerHeight) - 10
    const rect = target.getBoundingClientRect()
    const delta = rect.top < top ? rect.top - top : rect.bottom > bottom ? rect.top - top : 0
    if (nested) owner.scrollTop += delta
    else window.scrollBy(0, delta)
  }
  function move(delta) {
    refresh()
    if (!matches.length) return
    index = index < 0 ? (delta > 0 ? 0 : matches.length - 1) : (index + delta + matches.length) % matches.length
    targetInvalidated = false
    updateHighlights(); render(); reveal(matches[index])
  }
  function render() {
    const c = coverage()
    scope.textContent = c.sectioned ? (c.complete ? `All ${c.total} ${c.total === 1 ? 'section' : 'sections'} searched` : `${c.loaded} of ${c.total} sections searched · Results cover loaded sections only.`) : c.complete ? 'Entire document searched' : 'Loading document…'
    if (c.limited) scope.textContent += ' Search result limit reached. Refine your search to replace all.'
    count.textContent = !input.value ? 'Enter a word or phrase' : matches.length ? (index < 0 ? `${matches.length}${c.limited ? '+' : ''} matches · Choose a match` : `${index + 1} of ${matches.length}${c.limited ? '+' : ''}`) : c.complete ? 'No matches' : 'No matches in loaded sections'
    ;['prev','next'].forEach(action => { bar.querySelector(`[data-action="${action}"]`).disabled = !matches.length })
    bar.querySelector('[data-action="replace"]').disabled = index < 0
    bar.querySelector('[data-action="replaceAll"]').disabled = !matches.length || !c.complete || c.limited
    bar.querySelector('.find-replace-replace-scope').textContent = !c.complete ? 'Replace all is available when every section is loaded.' : c.limited ? 'Refine your search before replacing all matches.' : `Replace all changes ${matches.length} ${matches.length === 1 ? 'match' : 'matches'} across the document.`
    if (targetInvalidated) bar.querySelector('.find-replace-replace-scope').textContent += ' The selected passage changed. Choose a match before replacing it.'
    const focused = resultList.contains(document.activeElement) ? Number(document.activeElement.dataset.match) : null
    resultList.replaceChildren()
    const visibleIndices = Array.from({length:Math.min(matches.length,40)},(_,i)=>i)
    if (index >= 40) visibleIndices.push(index)
    visibleIndices.forEach(i => {
      const match = matches[i], button = document.createElement('button'), label = document.createElement('strong'), excerpt = document.createElement('span')
      button.type = 'button'; button.className = 'find-replace-result'; button.dataset.match = i
      button.setAttribute('aria-current', String(i === index))
      const frame = match.editor.view.dom.closest('turbo-frame')
      let heading = ''
      match.editor.state.doc.descendants((node,pos) => { if (node.type.name === 'heading' && pos < match.from) heading = node.textContent })
      label.textContent = heading || (frame ? `Section ${frames().indexOf(frame) + 1}` : 'Document')
      excerpt.textContent = match.editor.state.doc.textBetween(Math.max(0,match.from-28),Math.min(match.editor.state.doc.content.size,match.to+65),' ')
      button.append(label,excerpt)
      button.addEventListener('click', () => {
        // Refresh first; positions can change after this result was displayed.
        const saved = match; refresh()
        const selected = matches.findIndex(item => item.editor === saved.editor && item.from === saved.from && item.to === saved.to)
        if (selected < 0) { input.focus(); return }
        index = selected; targetInvalidated = false; updateHighlights(); render()
        const breakpoint = Number(container.closest('[data-editor-sidebar-breakpoint-value]')?.dataset.editorSidebarBreakpointValue) || 1400
        if (mount && innerWidth < breakpoint) {
          bar.dispatchEvent(new CustomEvent('editor:close-panel',{bubbles:true}))
          saved.editor.commands.setTextSelection({from:saved.from,to:saved.to})
          saved.editor.commands.focus(null,{scrollIntoView:false})
        }
        reveal(saved)
      })
      resultList.append(button)
    })
    details.querySelector('summary').textContent = `Passage results (${matches.length}${c.limited ? '+' : ''})${matches.length > 40 ? ' · first 40' : ''}`
    if (focused !== null) (resultList.querySelector(`[data-match="${focused}"]`) || input).focus({preventScroll:true})
  }
  function replace(all) {
    refresh()
    const c = coverage()
    if (!matches.length || (!all && index < 0) || (all && (!c.complete || c.limited))) return
    const nextIndex = index
    replacing = true
    try {
      if (all) liveEditors().forEach(editor => editor.commands.replaceAllMatches(replacement.value))
      else matches[index].editor.commands.replaceCurrentMatch(replacement.value)
    } finally { replacing = false }
    refresh({preserve:false})
    if (matches.length) {
      index = Math.max(0, Math.min(nextIndex, matches.length - 1))
      updateHighlights(); render(); reveal(matches[index])
    }
  }
  function toggleReplace(force) {
    const show = force === undefined ? replaceRow.classList.contains('d-none') : force
    replaceRow.classList.toggle('d-none',!show)
    bar.querySelector('[data-action="toggleReplace"]').setAttribute('aria-expanded',String(show))
    if (show) replacement.focus()
  }
  function close({restoreFocus=true} = {}) {
    if (closed) return
    closed = true; clearTimeout(timer)
    watched.forEach(editor => {editor.off('transaction',trackDocumentChange);if (!editor.isDestroyed) editor.commands.clearSearch()})
    watched.clear(); observer?.disconnect()
    ;['tiptap:section-loaded','tiptap:editor-ready'].forEach(name=>document.removeEventListener(name,scheduleRefresh))
    document.removeEventListener('turbo:before-cache', beforeCache)
    sidebar?.removeEventListener('editor:focus-changed', focusChanged)
    bar.remove(); if (activeBar?.element === bar) activeBar = null
    onClose?.()
    if (restoreFocus) {
      if (returnFocus?.isConnected && returnFocus !== document.body && !returnFocus.closest('.find-replace-bar')) returnFocus.focus({preventScroll:true})
      else if (returnEditor && !returnEditor.isDestroyed) returnEditor.commands.focus(null,{scrollIntoView:false})
    }
  }
  input.addEventListener('input',()=>{clearTimeout(timer);timer=setTimeout(()=>refresh({navigate:true,preserve:false}),200)})
  bar.addEventListener('keydown',event=>{
    if (event.key==='Escape') {event.preventDefault();event.stopPropagation();close()}
    else if (event.key==='Enter' && event.target===input) {event.preventDefault();move(event.shiftKey?-1:1)}
    else if (event.key==='Enter' && event.target===replacement) {event.preventDefault();replace(false)}
  })
  bar.querySelectorAll('[data-action]').forEach(button=>button.addEventListener('click',()=>{
    const action=button.dataset.action
    if(action==='close')close()
    if(action==='next')move(1)
    if(action==='prev')move(-1)
    if(action==='replace')replace(false)
    if(action==='replaceAll')replace(true)
    if(action==='toggleReplace')toggleReplace()
    if(action==='case'){caseSensitive=!caseSensitive;button.setAttribute('aria-pressed',String(caseSensitive));refresh({navigate:true,preserve:false})}
  }))
  const observer = container.querySelector('.sections-list') ? new MutationObserver(scheduleRefresh) : null
  const sidebar = container.closest('[data-controller~="editor-sidebar"]')
  const beforeCache = () => close({restoreFocus:false})
  const focusChanged = () => {
    if (mount && document.body.classList.contains('editor-focus-mode')) close({restoreFocus:false})
  }
  document.addEventListener('turbo:before-cache', beforeCache)
  sidebar?.addEventListener('editor:focus-changed', focusChanged)
  observer?.observe(container.querySelector('.sections-list'),{childList:true})
  ;['tiptap:section-loaded','tiptap:editor-ready'].forEach(name=>document.addEventListener(name,scheduleRefresh))
  if (mount) mount.append(bar)
  else {const toolbar=container.querySelector('.tiptap-fixed-toolbar');if(toolbar)toolbar.after(bar);else container.prepend(bar)}
  activeBar = {element:bar,container,mount,close,refresh,focus(withReplace){onFocus?.();refresh();if(withReplace)toggleReplace(true);else input.focus();input.select()}}
  refresh(); onFocus?.(); input.focus()
  return activeBar
}
